import { Controller } from "@hotwired/stimulus"

// The DPFO typ B wizard: shows the parts of a step its answers open, adds and
// removes children, and formats what is typed. It computes nothing — every
// amount of the return is worked out on the server.
export default class extends Controller {
  static targets = [
    "form",
    "dicInput",
    "dicError",
    // Sections opened by a switch
    "employedFields",
    "dohodaFields",
    "rentFields",
    "oslobodenieField",
    "partnerBonusFields",
    "partnerFields",
    "pensionFields",
    "mortgageFields",
    "twoPercentFields",
    "bankAccountFields",
    // Children (r. 33)
    "childrenFields",
    "childrenList",
    "child",
    "childTemplate",
    "addChildButton",
    // Parents (§ 50aa, r. 153)
    "parentAFields",
    "parentBFields",
    "parentAdoption"
  ]

  static values = {
    maxChildren: { type: Number, default: 20 },
    texts: { type: Object, default: {} }
  }

  // The switch's id names the section it opens.
  static SECTIONS = {
    employed: "employedFields",
    dohoda: "dohodaFields",
    rent: "rentFields",
    partner_bonus_na_deti: "partnerBonusFields",
    r032_uplatnujem_na_partnera: "partnerFields",
    platil_prispevky_na_dochodok: "pensionFields",
    r035_uplatnuje_uroky: "mortgageFields",
    XIIoddiel_uplatnujem2percenta: "twoPercentFields",
    ziadamVyplatitDanovyBonusUrokPreplatok: "bankAccountFields"
  }

  connect() {
    this.childCounter = this.childTargets.length
    this.refreshChildren()
  }

  // Keeps a step with an empty required field from going to the server, which
  // checks every field again anyway.
  // The form is novalidate, which keeps the browser's unlocalised bubbles
  // away and with them its checks: what the fields declare is checked here and
  // said under the field. The server checks the step again.
  validate(event) {
    let valid = true
    this.formTarget.querySelectorAll("[required], [pattern]").forEach((field) => {
      if (field.type === "checkbox" || field.type === "radio") return

      const message = this.problemWith(field)
      this.markField(field, message)
      if (message) valid = false
    })
    if (!valid) event.preventDefault()
  }

  // An empty required field, or a value its pattern rejects — the field's
  // title says what it wants. Closed sections and disabled fields are not sent.
  problemWith(field) {
    if (field.disabled || field.closest(".d-none")) return null
    if (field.required && field.value.trim() === "") return this.textsValue.required
    if (field.validity.valid || !field.title) return null
    return field.title
  }

  toggleSection(event) {
    const target = this.constructor.SECTIONS[event.target.id]
    if (target && this[`has${this.capitalize(target)}Target`]) {
      this[`${target}Target`].classList.toggle("d-none", !event.target.checked)
    }
  }

  toggleOslobodenie(event) {
    if (this.hasOslobodenieFieldTarget) {
      this.oslobodenieFieldTarget.classList.toggle("d-none", !event.target.checked)
    }
  }

  // "Áno" opens the children; the other two answers file none.
  toggleChildren(event) {
    if (this.hasChildrenFieldsTarget) {
      this.childrenFieldsTarget.classList.toggle("d-none", event.target.value !== "yes")
    }
  }

  // "Celý rok" makes the single months irrelevant.
  toggleChildMonths(event) {
    const months = event.target.closest(".child-entry")?.querySelector("[data-child-months]")
    if (months) months.classList.toggle("d-none", event.target.checked)
  }

  toggleParents(event) {
    const choice = event.target.value
    if (this.hasParentAFieldsTarget) this.parentAFieldsTarget.classList.toggle("d-none", choice === "nie")
    if (this.hasParentBFieldsTarget) this.parentBFieldsTarget.classList.toggle("d-none", choice !== "obidvom")
    if (this.hasParentAdoptionTarget) this.parentAdoptionTarget.classList.toggle("d-none", choice === "nie")
  }

  // A blank child is cloned from the server-rendered <template>; only the
  // index in its name/id/for attributes is rewritten. Indexes only grow, so a
  // removed child never hands its index to a new one; the server orders the
  // rows by it.
  addChild() {
    if (!this.hasChildTemplateTarget || !this.hasChildrenListTarget) return
    if (this.childTargets.length >= this.maxChildrenValue) return

    const index = String(this.childCounter++)
    const fragment = this.childTemplateTarget.content.cloneNode(true)
    fragment.querySelectorAll("[name], [id], [for]").forEach((element) => {
      for (const attribute of ["name", "id", "for"]) {
        const current = element.getAttribute(attribute)
        if (current) element.setAttribute(attribute, current.replaceAll("__INDEX__", index))
      }
    })

    this.childrenListTarget.appendChild(fragment)
    this.refreshChildren()
  }

  removeChild(event) {
    event.target.closest(".child-entry")?.remove()
    this.refreshChildren()
  }

  // Numbers the children by position and stops adding at the schema's limit.
  refreshChildren() {
    const title = this.textsValue.childTitle
    this.childTargets.forEach((child, position) => {
      const heading = child.querySelector("[data-child-title]")
      if (heading && title) heading.textContent = title.replace("%{number}", position + 1)
    })
    if (this.hasAddChildButtonTarget) {
      this.addChildButtonTarget.disabled = this.childTargets.length >= this.maxChildrenValue
    }
    // The last card stays: "Nie" is the way to claim no child.
    const lonely = this.childTargets.length <= 1
    this.childTargets.forEach((child) => {
      const remove = child.querySelector("[data-action~='click->finance-management--tax-form#removeChild']")
      if (remove) remove.disabled = lonely
    })
  }

  // Format PSC (postal code) - add space after 3rd digit
  formatPsc(event) {
    let value = event.target.value.replace(/\D/g, "")
    if (value.length > 3) {
      value = `${value.slice(0, 3)} ${value.slice(3, 5)}`
    }
    event.target.value = value
  }

  // Format rodne cislo - add " / " after 6th digit
  formatRodneCislo(event) {
    let value = event.target.value.replace(/\D/g, "")
    if (value.length > 6) {
      value = `${value.slice(0, 6)} / ${value.slice(6, 10)}`
    }
    event.target.value = value
  }

  // Format IBAN - add spaces every 4 characters
  formatIban(event) {
    const value = event.target.value.replace(/\s/g, "").toUpperCase()
    event.target.value = value.match(/.{1,4}/g)?.join(" ") ?? ""
  }

  // Allow only digits and one decimal separator with at most two decimals.
  formatCurrency(event) {
    let value = event.target.value.replace(/[^0-9,.]/g, "").replace(",", ".")
    const parts = value.split(".")
    if (parts.length > 2) {
      value = `${parts[0]}.${parts.slice(1).join("")}`
    }
    const [whole, fraction] = value.split(".")
    if (fraction !== undefined && fraction.length > 2) {
      value = `${whole}.${fraction.slice(0, 2)}`
    }
    event.target.value = value
  }

  // A DIČ is ten digits; the server checks it again on submit.
  validateDic(event) {
    if (!this.hasDicErrorTarget) return

    const value = event.target.value
    const invalid = value !== "" && !/^\d{10}$/.test(value.replace(/\D/g, ""))
    this.dicErrorTarget.textContent = invalid ? (this.textsValue.dicInvalid ?? "") : ""
    this.dicErrorTarget.classList.toggle("d-none", !invalid)
    event.target.classList.toggle("is-invalid", invalid)
  }

  // A field marked by #validate is cleared as soon as something is typed in,
  // or, when it is optional, emptied. The DIČ keeps its own format check,
  // which ran first on the same input.
  clearInvalid(event) {
    const field = event.target
    if (!field.classList.contains("is-invalid") || (field.required && field.value.trim() === "")) return

    this.markField(field, null)
    if (this.hasDicInputTarget && field === this.dicInputTarget) this.validateDic({ target: field })
  }

  // Shows `message` under the field, or clears it when there is none.
  markField(field, message) {
    field.classList.toggle("is-invalid", Boolean(message))
    const container = field.closest(".lc-input")
    if (!container) return

    let hint = container.querySelector(".form-hint.text-danger")
    if (message && !hint) {
      hint = document.createElement("div")
      hint.className = "form-hint text-danger"
      container.appendChild(hint)
    }
    if (hint) {
      hint.textContent = message ?? ""
      hint.classList.toggle("d-none", !message)
    }
  }

  capitalize(string) {
    return string.charAt(0).toUpperCase() + string.slice(1)
  }
}
